import { Button, TextField } from "@dash/form";
import { Card, SectionHeader } from "@dash/page";
import { Spinner } from "@dash/spinner";
import type { FC } from "react";
import { MdCheck } from "react-icons/md";

import type { TicketCategory } from "@megaron/support-contracts";

import { AttachmentEntry, SupportAttachmentPicker } from "@megarax/crm-customers";

import { SupportCreateTicketFormFields } from "./SupportCreateTicketFormFields";
import type { TicketProductDraft } from "../products/SupportTicketProductsField";
import { supportRequiredFieldLabel } from "../shared/supportRequiredFieldLabel";
import {
  supportFormAttachmentsSectionCss,
  supportFormCardCss,
  supportFormFieldCss,
  supportFormSidebarCss,
} from "./supportCreateTicketFormStyles";

type ContactOption = {
  label: string;
  value: string;
  email: string;
  name: string;
};

type MailboxOption = {
  label: string;
  value: string;
};

type Props = {
  isMobile: boolean;
  subject: string;
  setSubject: (value: string) => void;
  message: string;
  setMessage: (value: string) => void;
  reporterName: string;
  setReporterName: (value: string) => void;
  reporterEmail: string;
  setReporterEmail: (value: string) => void;
  selectedContact: string;
  onContactChange: (value: string) => void;
  sourceMailbox: string;
  setSourceMailbox: (value: string) => void;
  mailboxOptions: MailboxOption[];
  isMailboxLoading: boolean;
  contactOptions: ContactOption[];
  hasCustomerContext: boolean;
  attachments: AttachmentEntry[];
  setAttachments: (attachments: AttachmentEntry[]) => void;
  selectedReasonIds: string[];
  setSelectedReasonIds: (value: string[]) => void;
  category: TicketCategory | null;
  setCategory: (value: TicketCategory | null) => void;
  products: TicketProductDraft[];
  setProducts: (value: TicketProductDraft[]) => void;
  invoiceNumber: string;
  setInvoiceNumber: (value: string) => void;
  onCreate: () => void;
  isBusy: boolean;
};

export const SupportCreateTicketFormBody: FC<Props> = ({
  isMobile,
  subject,
  setSubject,
  attachments,
  setAttachments,
  onCreate,
  isBusy,
  ...fieldProps
}) => {
  const attachmentsSection = (
    <div css={supportFormAttachmentsSectionCss}>
      <SectionHeader isHr css={{ margin: 0 }}>
        Załączniki
      </SectionHeader>
      <SupportAttachmentPicker attachments={attachments} onChange={setAttachments} />
    </div>
  );

  return (
    <div
      css={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        gap: "1rem",
        alignItems: isMobile ? "flex-start" : "stretch",
        width: "100%",
      }}
    >
      <div
        css={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          flex: "1 1 auto",
          minWidth: 0,
          width: "100%",
          alignSelf: "stretch",
        }}
      >
        {isMobile ? attachmentsSection : null}
        <Card variant="secondary" css={supportFormCardCss}>
          <SupportCreateTicketFormFields {...fieldProps} />
        </Card>
        <Card variant="secondary" css={supportFormCardCss}>
          <TextField
            label={supportRequiredFieldLabel("Tytuł")}
            value={subject}
            onChange={setSubject}
            css={supportFormFieldCss}
          />
          <Button
            isDisabled={isBusy}
            icon={isBusy ? <Spinner size="16px" color="white" /> : <MdCheck size={20} />}
            css={{ width: "100%", marginTop: "0.375rem" }}
            onClick={onCreate}
          >
            Utwórz zgłoszenie
          </Button>
        </Card>
      </div>
      {!isMobile ? <div css={supportFormSidebarCss(isMobile)}>{attachmentsSection}</div> : null}
    </div>
  );
};
